import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Settings } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  sortable: boolean;
  width: string;
  frozen?: boolean;
}

interface ColumnConfigDropdownProps {
  columns: ColumnConfig[];
  onColumnsChange: (columns: ColumnConfig[]) => void;
}

export const ColumnConfigDropdown: React.FC<ColumnConfigDropdownProps> = ({
  columns,
  onColumnsChange,
}) => {
  const [moveInterval, setMoveInterval] = useState<NodeJS.Timeout | null>(null);
  const [isMoving, setIsMoving] = useState<{index: number, direction: 'up' | 'down'} | null>(null);

  const toggleColumnVisibility = (columnKey: string) => {
    const newColumns = columns.map(col => 
      col.key === columnKey ? { ...col, visible: !col.visible } : col
    );
    onColumnsChange(newColumns);
  };

  const moveColumn = (fromIndex: number, toIndex: number) => {
    const newColumns = [...columns];
    const [movedColumn] = newColumns.splice(fromIndex, 1);
    newColumns.splice(toIndex, 0, movedColumn);
    onColumnsChange(newColumns);
  };

  const startMoveColumn = (fromIndex: number, direction: 'up' | 'down') => {
    setIsMoving({ index: fromIndex, direction });
    
    const move = () => {
      const newColumns = [...columns];
      const currentIndex = newColumns.findIndex(col => col.key === columns[fromIndex].key);
      const toIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      
      if (toIndex >= 0 && toIndex < newColumns.length) {
        const [movedColumn] = newColumns.splice(currentIndex, 1);
        newColumns.splice(toIndex, 0, movedColumn);
        onColumnsChange(newColumns);
      }
    };

    // Initial move
    move();

    // Set up interval for continuous movement
    const interval = setInterval(move, 150); // Move every 150ms
    setMoveInterval(interval);
  };

  const stopMoveColumn = () => {
    setIsMoving(null);
    if (moveInterval) {
      clearInterval(moveInterval);
      setMoveInterval(null);
    }
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (moveInterval) {
        clearInterval(moveInterval);
      }
    };
  }, [moveInterval]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="flex items-center gap-2">
          <Settings className="h-4 w-4" />
          Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <div className="px-3 py-2 text-sm font-semibold border-b">Column Settings</div>
        <div className="max-h-96 overflow-y-auto">
          {columns.map((column, index) => (
            <div key={column.key} className="px-3 py-2 hover:bg-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="checkbox"
                  checked={column.visible}
                  onChange={() => toggleColumnVisibility(column.key)}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm">{column.label}</span>
              </div>
              <div className="flex gap-1">
                {index > 0 && (
                  <button
                    type="button"
                    className={`h-6 w-6 p-0 rounded flex items-center justify-center text-sm transition-colors ${
                      isMoving?.index === index && isMoving?.direction === 'up'
                        ? 'bg-blue-200 text-blue-700'
                        : 'hover:bg-blue-100'
                    }`}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      startMoveColumn(index, 'up');
                    }}
                    onMouseUp={(e) => {
                      e.stopPropagation();
                      stopMoveColumn();
                    }}
                    onMouseLeave={(e) => {
                      e.stopPropagation();
                      stopMoveColumn();
                    }}
                    title="Hold to move up quickly"
                  >
                    ↑
                  </button>
                )}
                {index < columns.length - 1 && (
                  <button
                    type="button"
                    className={`h-6 w-6 p-0 rounded flex items-center justify-center text-sm transition-colors ${
                      isMoving?.index === index && isMoving?.direction === 'down'
                        ? 'bg-blue-200 text-blue-700'
                        : 'hover:bg-blue-100'
                    }`}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      startMoveColumn(index, 'down');
                    }}
                    onMouseUp={(e) => {
                      e.stopPropagation();
                      stopMoveColumn();
                    }}
                    onMouseLeave={(e) => {
                      e.stopPropagation();
                      stopMoveColumn();
                    }}
                    title="Hold to move down quickly"
                  >
                    ↓
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
